import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@lib/api/client';
import { useCompanies } from '@features/companies/hooks/useCompanies';
import { GuidedTour, type GuidedStep } from './GuidedTour';
import { captureScreenMap, type ScreenElement } from '../lib/captureScreenMap';
import { useShowFeedbackToast, useSupportConfig } from '../SupportErrorProvider';
import { useDraggable } from '../lib/useDraggable';
import { Markdown } from './Markdown';

interface SupportResult {
  reply: string;
  session_id: string;
  interaction_id?: string;
  steps?: GuidedStep[];
}

// Un turno del chat. `steps` (si vienen) habilitan el botón "Guíame en pantalla".
interface ChatMessage {
  id: number;
  role: 'user' | 'assistant';
  text: string;
  steps?: GuidedStep[];
  error?: boolean;
}

// Tareas frecuentes ("guided choice first, free text second"): el cliente arranca
// de un ejemplo en vez de una hoja en blanco. Al tocar uno, se envía directo.
const QUICK_PROMPTS = [
  'Quiero crear un cliente',
  'Quiero emitir una factura',
  'Quiero agregar un producto',
  '¿Por qué me falló una factura?',
];

/** Carita de robot del asistente (se usa en el avatar en varios tamaños). */
function BotIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="8" width="16" height="11" rx="4" />
      <circle cx="9.5" cy="13.5" r="1.15" fill="currentColor" stroke="none" />
      <circle cx="14.5" cy="13.5" r="1.15" fill="currentColor" stroke="none" />
      <path d="M12 4.8V8" />
      <circle cx="12" cy="3.6" r="1.3" />
    </svg>
  );
}

/** Tres puntitos animados: "el asistente está escribiendo". */
function TypingDots() {
  return (
    <span className="flex items-center gap-1">
      {[0, 150, 300].map((d) => (
        <span key={d} className="h-2 w-2 animate-bounce rounded-full bg-secondary-400" style={{ animationDelay: `${d}ms` }} />
      ))}
    </span>
  );
}

/**
 * Asistente de soporte como CHAT: botón flotante que abre una ventana de
 * conversación (hilo de mensajes, indicador de escritura, composer). Cada
 * respuesta puede traer pasos y ofrecer "Guíame en pantalla" (recorrido guiado).
 * Al abrir se captura la pantalla ANTES del modal para no fotografiarlo.
 */
export function SupportReportButton() {
  const { selectedCompany } = useCompanies();
  const showFeedbackToast = useShowFeedbackToast();
  const { assistantEnabled, guideEnabled } = useSupportConfig();
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<string | null>(null);
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const [screenMap, setScreenMap] = useState<ScreenElement[]>([]);
  const [lastMessage, setLastMessage] = useState('');
  const [tour, setTour] = useState<{ steps: GuidedStep[]; sessionId?: string } | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const msgId = useRef(0);
  const nextId = () => (msgId.current += 1);

  // El composer crece con el texto (hasta un tope) y vuelve a una línea al vaciarse.
  function autosize() {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 128) + 'px';
  }

  // Botón flotante arrastrable; arranca abajo-IZQUIERDA y recuerda dónde lo dejes.
  const { pos, dragHandlers, wasDragged } = useDraggable('fl-support-help-btn', (_vw, vh) => ({ x: 20, y: vh - 72 }));
  // La ventana del chat también es arrastrable (por su encabezado). Arranca
  // abajo-DERECHA y recuerda dónde la dejes.
  const PANEL_W = 348;
  const PANEL_H = 540;
  const panelDrag = useDraggable(
    'fl-support-panel',
    (vw, vh) => ({ x: vw - PANEL_W - 20, y: vh - PANEL_H - 20 }),
    { w: PANEL_W, h: PANEL_H }
  );
  // En pantallas chicas el chat es un bottom-sheet a lo ancho (no una tarjeta
  // flotante/arrastrable): más cómodo en móvil y no se sale de pantalla.
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  // Cartel "se puede mover" la PRIMERA vez (se recuerda que ya se vio).
  const [showMoveHint, setShowMoveHint] = useState(() => {
    try { return !localStorage.getItem('fl-support-help-hint'); } catch { return false; }
  });
  const dismissMoveHint = () => {
    setShowMoveHint(false);
    try { localStorage.setItem('fl-support-help-hint', '1'); } catch { /* ignore */ }
  };
  // Auto-ocultar el cartel a los 9s.
  useEffect(() => {
    if (!showMoveHint) return;
    const t = setTimeout(dismissMoveHint, 9000);
    return () => clearTimeout(t);
  }, [showMoveHint]);

  // Aviso "el chat se puede mover" — la PRIMERA vez que se abre (se recuerda).
  const [showPanelHint, setShowPanelHint] = useState(false);
  const dismissPanelHint = () => {
    setShowPanelHint(false);
    try { localStorage.setItem('fl-support-panel-hint', '1'); } catch { /* ignore */ }
  };
  useEffect(() => {
    if (!open || isMobile) return; // en móvil no se arrastra: no mostramos el aviso
    try { if (localStorage.getItem('fl-support-panel-hint')) return; } catch { return; }
    setShowPanelHint(true);
    const t = setTimeout(dismissPanelHint, 7000);
    return () => clearTimeout(t);
  }, [open, isMobile]);

  // Permite abrir el asistente desde cualquier parte del portal (ej. el banner del
  // Dashboard) disparando `window.dispatchEvent(new CustomEvent('fl:open-support'))`.
  useEffect(() => {
    const onOpen = () => { if (assistantEnabled && !open) openReporter(); };
    window.addEventListener('fl:open-support', onOpen);
    return () => window.removeEventListener('fl:open-support', onOpen);
  }, [assistantEnabled, open]);

  // Cerrar con Escape (ya no hay fondo oscuro donde hacer clic afuera).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  // Auto-scroll al último mensaje / al indicador de escritura.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [messages, sending, open]);

  async function openReporter() {
    setCapturing(true);
    // Mapa de la pantalla ANTES de abrir el modal (mapea la página real de abajo).
    setScreenMap(captureScreenMap());
    try {
      const { default: html2canvas } = await import('html2canvas');
      // Escala 1 para no inflar el tamaño; el agente acepta máx 5 MB por imagen.
      const canvas = await html2canvas(document.body, {
        logging: false,
        useCORS: true,
        scale: 1,
      });
      // JPEG con calidad adaptativa: bajamos la calidad hasta quedar < 4.5 MB.
      const LIMIT = 4.5 * 1024 * 1024;
      let dataUrl = '';
      for (const q of [0.7, 0.5, 0.35, 0.25]) {
        dataUrl = canvas.toDataURL('image/jpeg', q);
        if (dataUrl.length * 0.75 < LIMIT) break;
      }
      setScreenshot(dataUrl);
    } catch {
      setScreenshot(null); // si falla la captura, igual se puede chatear solo con texto
    } finally {
      setCapturing(false);
      setOpen(true);
    }
  }

  function close() {
    // Cerramos la ventana pero conservamos el hilo: al reabrir, la charla sigue.
    setOpen(false);
  }

  function resetChat() {
    setMessages([]);
    setSessionId(undefined);
    setMessage('');
    requestAnimationFrame(autosize);
  }

  async function send(textArg?: string) {
    const text = (textArg ?? message).trim();
    if (!text || sending) return;
    setLastMessage(text); // objetivo para la guía iterativa
    setMessage('');
    requestAnimationFrame(autosize);
    setMessages((m) => [...m, { id: nextId(), role: 'user', text }]);
    setSending(true);
    try {
      const images = screenshot
        ? [{ mime_type: 'image/jpeg', data_base64: screenshot.split(',', 2)[1] }]
        : [];
      const { data } = await apiClient.post<SupportResult>(
        '/support/report',
        {
          message: text,
          sessionId,
          context: {
            url: window.location.href,
            path: window.location.pathname,
            user_agent: navigator.userAgent,
            // Mapa de la pantalla (capturado antes de abrir el modal).
            screen_elements: screenMap,
            // Identidad de la compañía → queda guardada con el reporte (identificador = bug + compañía).
            company_name: selectedCompany?.name || selectedCompany?.fullName || undefined,
            company_legal_name: selectedCompany?.fullName || undefined,
            company_ruc: selectedCompany?.ruc || selectedCompany?.numberId || undefined,
            company_dv: selectedCompany?.dv || undefined,
            company_id: selectedCompany?.id || undefined,
          },
          images,
        },
        // El agente puede tardar (análisis de imagen + búsqueda + pasos guiados);
        // damos margen por encima del timeout del backend (120s) para recibir la respuesta.
        { timeout: 130000 }
      );
      setMessages((m) => [...m, { id: nextId(), role: 'assistant', text: data.reply, steps: data.steps }]);
      setSessionId(data.session_id);
      if (data.interaction_id) showFeedbackToast(data.interaction_id, text.slice(0, 40));
    } catch (e: any) {
      const detail = e?.response?.data?.message;
      setMessages((m) => [
        ...m,
        { id: nextId(), role: 'assistant', error: true, text: detail || 'No pude procesar tu mensaje. Intenta de nuevo en un momento.' },
      ]);
    } finally {
      setSending(false);
    }
  }

  // Arranca el recorrido guiado con los pasos de una respuesta puntual.
  function startTour(steps: GuidedStep[]) {
    setOpen(false);
    setTour({ steps, sessionId });
  }

  // Si el asistente está apagado para este proyecto, no mostramos nada (portal normal).
  if (!assistantEnabled) return null;

  const empty = messages.length === 0 && !sending;

  return (
    <>
      {/* Cartel "se puede mover" — solo la primera vez. Aparece arriba del botón. */}
      {showMoveHint && (
        <div
          data-support-ui
          style={{ left: pos.x, top: Math.max(8, pos.y - 52), maxWidth: 260 }}
          className="fixed z-[55] flex items-center gap-2 rounded-xl bg-secondary-900 px-3 py-2 text-xs font-medium text-white shadow-xl animate-in fade-in slide-in-from-bottom-2"
        >
          <span>✋ Arrástrame para moverme a donde quieras</span>
          <button onClick={dismissMoveHint} aria-label="Entendido" className="shrink-0 rounded-md px-1 text-white/70 hover:text-white">✕</button>
        </div>
      )}

      {/* Botón flotante circular: alterna abrir/cerrar el chat (arrastrable). Cuando
          el chat está abierto se convierte en una X para cerrar todo. */}
      <button
        type="button"
        onClick={() => { if (!wasDragged()) { if (open) close(); else openReporter(); } }}
        disabled={capturing}
        title={open ? 'Cerrar chat de ayuda' : '¿Necesitas ayuda? Chatea con el asistente — arrástrame para moverme'}
        aria-label={open ? 'Cerrar chat de ayuda' : 'Abrir chat de ayuda'}
        data-support-ui
        {...dragHandlers}
        onPointerDown={(e) => { dismissMoveHint(); dragHandlers.onPointerDown(e); }}
        style={{ left: pos.x, top: pos.y, touchAction: 'none' }}
        className={`fixed z-[61] flex h-14 w-14 items-center justify-center rounded-full bg-primary-700 text-white shadow-hard transition-transform hover:scale-105 hover:bg-primary-800 disabled:opacity-70 cursor-grab active:cursor-grabbing motion-reduce:animate-none ${open ? '' : 'animate-help-pulse'}`}
      >
        {capturing ? (
          // Spinner mientras captura la pantalla
          <svg className="h-6 w-6 animate-spin" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M21 12a9 9 0 1 1-6.219-8.56" />
          </svg>
        ) : open ? (
          // X para cerrar
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 6 6 18M6 6l12 12" />
          </svg>
        ) : (
          // Ícono de chat/ayuda
          <svg className="h-7 w-7" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2Z" />
            <path d="M8 10h.01M12 10h.01M16 10h.01" />
          </svg>
        )}
      </button>

      {/* Ventana de chat — panel FLOTANTE y ARRASTRABLE (no modal centrado): no
          oscurece la pantalla ni tapa el contenido, así se puede seguir trabajando
          y ver el recorrido guiado. Se mueve arrastrando su encabezado. */}
      {open && (
        <div
          data-support-ui
          style={isMobile ? undefined : { left: panelDrag.pos.x, top: panelDrag.pos.y, width: PANEL_W, height: PANEL_H }}
          className={
            isMobile
              ? 'fixed inset-x-0 bottom-0 z-[60] flex h-[88vh] max-h-[88vh] w-full flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl ring-1 ring-black/5 animate-in fade-in slide-in-from-bottom-4'
              : 'fixed z-[60] flex max-h-[calc(100vh-16px)] max-w-[calc(100vw-16px)] flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/5 animate-in fade-in slide-in-from-bottom-4'
          }
        >
          {/* Aviso de una sola vez: el chat se puede mover (solo escritorio). */}
          {showPanelHint && !isMobile && (
            <div className="pointer-events-none absolute left-1/2 top-14 z-10 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-900/95 px-3 py-1.5 text-xs font-medium text-white shadow-lg animate-in fade-in slide-in-from-top-1">
              <span aria-hidden="true">✋</span> Arrastra desde aquí para mover el chat
            </div>
          )}

          {/* Encabezado = asa para mover el panel (solo en escritorio; en móvil es
              un bottom-sheet fijo y no se arrastra). */}
          <div
            {...(isMobile ? {} : panelDrag.dragHandlers)}
            onPointerDown={isMobile ? undefined : (e) => { dismissPanelHint(); panelDrag.dragHandlers.onPointerDown(e); }}
            style={isMobile ? undefined : { touchAction: 'none' }}
            title={isMobile ? undefined : 'Arrástrame para mover el chat'}
            className={`relative flex items-center gap-3 bg-gradient-to-r from-primary-700 to-primary-900 px-4 pb-3 pt-4 text-white ${isMobile ? '' : 'cursor-grab active:cursor-grabbing'}`}
          >
            {/* Barra de arrastre / asa del sheet (afordancia visual de "se puede mover") */}
            <span className="absolute left-1/2 top-1.5 h-1 w-10 -translate-x-1/2 rounded-full bg-white/35" aria-hidden="true" />
              <div className="relative">
                <span className="absolute inset-0 rounded-full bg-fuchsia-500/50 blur-md" aria-hidden="true" />
                <span className="relative flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-primary-500 text-white ring-2 ring-white/25">
                  <BotIcon className="h-5 w-5" />
                </span>
              </div>
              <div className="flex-1">
                <h2 className="text-sm font-bold leading-tight">Asistente de soporte</h2>
                <p className="flex items-center gap-1.5 text-xs text-white/70">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" /> En línea
                </p>
              </div>
              {messages.length > 0 && (
                <button
                  onClick={resetChat}
                  onPointerDown={(e) => e.stopPropagation()}
                  title="Nueva conversación"
                  aria-label="Nueva conversación"
                  className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
                >
                  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                    <path d="M3 3v5h5" />
                  </svg>
                </button>
              )}
              <button
                onClick={close}
                onPointerDown={(e) => e.stopPropagation()}
                aria-label="Cerrar"
                className="rounded-lg p-1.5 text-white/70 transition hover:bg-white/10 hover:text-white"
              >
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Hilo de mensajes */}
            <div ref={scrollRef} className="flex-1 space-y-4 overflow-y-auto bg-secondary-50/60 px-4 py-4">
              {empty ? (
                /* Estado inicial: saludo con avatar y tareas frecuentes (estilo referencia). */
                <div className="flex h-full flex-col items-center justify-center gap-5 text-center">
                  <div className="relative">
                    <span className="absolute inset-0 scale-[1.35] animate-pulse rounded-full bg-fuchsia-500/40 blur-xl" aria-hidden="true" />
                    <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-primary-600 text-white shadow-lg">
                      <BotIcon className="h-8 w-8" />
                    </span>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-gray-900">¡Hola! ¿Con qué te ayudo?</p>
                    <p className="mt-1 text-sm text-secondary-500">
                      Pregúntame lo que necesites o empieza con una de estas:
                    </p>
                  </div>
                  <div className="flex flex-wrap justify-center gap-2">
                    {QUICK_PROMPTS.map((q) => (
                      <button
                        key={q}
                        type="button"
                        onClick={() => send(q)}
                        className="rounded-full border border-primary-200 bg-white px-3 py-1.5 text-sm font-medium text-primary-700 shadow-sm transition hover:border-primary-300 hover:bg-primary-50"
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  {messages.map((m) =>
                    m.role === 'user' ? (
                      <div key={m.id} className="flex justify-end animate-in fade-in slide-in-from-bottom-2">
                        <div className="max-w-[82%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-primary-700 px-3.5 py-2 text-sm leading-relaxed text-white shadow-sm">
                          {m.text}
                        </div>
                      </div>
                    ) : (
                      <div key={m.id} className="flex items-start gap-2 animate-in fade-in slide-in-from-bottom-2">
                        <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-primary-500 text-white">
                          <BotIcon className="h-4 w-4" />
                        </span>
                        <div className="min-w-0 max-w-[82%] space-y-2">
                          <div className={`rounded-2xl rounded-bl-md px-3.5 py-2 text-sm leading-relaxed shadow-sm ${m.error ? 'bg-danger-50 text-danger-700' : 'bg-white text-gray-800'}`}>
                            <Markdown text={m.text} />
                          </div>
                          {m.steps && m.steps.length > 0 && (
                            <div className="rounded-2xl border border-secondary-200 bg-white p-3 shadow-sm">
                              <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-primary-500">Pasos sugeridos</p>
                              <ol className="list-decimal space-y-1 pl-5 text-sm text-gray-700 marker:font-semibold marker:text-primary-500">
                                {m.steps.map((s, i) => (
                                  <li key={i}>{s.label || `${s.action} ${s.target ?? ''}`}</li>
                                ))}
                              </ol>
                              {guideEnabled && (
                                <button
                                  onClick={() => startTour(m.steps!)}
                                  className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-primary-700 to-primary-600 py-2 text-sm font-semibold text-white shadow-sm transition hover:from-primary-800 hover:to-primary-700"
                                >
                                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="3 11 22 2 13 21 11 13 3 11" />
                                  </svg>
                                  Guíame en pantalla
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    )
                  )}

                  {sending && (
                    <div className="flex items-start gap-2 animate-in fade-in">
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-primary-500 text-white">
                        <BotIcon className="h-4 w-4" />
                      </span>
                      <div className="rounded-2xl rounded-bl-md bg-white px-4 py-3 shadow-sm">
                        <TypingDots />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Composer anclado abajo (estilo chat): campo que crece + botón de envío. */}
            <div className="border-t border-secondary-100 bg-white p-3">
              <div className="flex items-end gap-2 rounded-2xl border border-secondary-300 bg-white px-2.5 py-1.5 transition focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-200">
                <textarea
                  ref={textareaRef}
                  value={message}
                  rows={1}
                  onChange={(e) => { setMessage(e.target.value); autosize(); }}
                  onKeyDown={(e) => {
                    // Enter envía; Shift+Enter (o ⌘/Ctrl+Enter) hace salto de línea.
                    if (e.key === 'Enter' && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  autoFocus
                  placeholder="Escribe tu mensaje…"
                  className="max-h-32 flex-1 resize-none bg-transparent px-1.5 py-1.5 text-sm leading-relaxed text-gray-900 outline-none placeholder:text-secondary-400"
                />
                <button
                  onClick={() => send()}
                  disabled={!message.trim() || sending}
                  aria-label="Enviar"
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-fuchsia-500 to-primary-600 text-white shadow-sm transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M22 2 11 13M22 2l-7 20-4-9-9-4 20-7Z" />
                  </svg>
                </button>
              </div>
              <p className="mt-1.5 text-center text-[11px] text-secondary-400">
                Enter para enviar · Shift+Enter para salto de línea
              </p>
            </div>
          </div>
      )}

      {/* Recorrido guiado en pantalla a partir de los pasos de una respuesta */}
      {tour && tour.steps.length > 0 && (
        <GuidedTour
          steps={tour.steps}
          sessionId={tour.sessionId}
          goal={lastMessage}
          onComplete={(journey) =>
            apiClient
              .post('/support/guide-complete', { sessionId: tour.sessionId, goal: lastMessage, journey })
              .catch(() => {})
          }
          onClose={() => setTour(null)}
        />
      )}
    </>
  );
}
